/*
|--------------------------------------------------------------------------
| Resourceful Error Middleware
|--------------------------------------------------------------------------
|
| Intercepts errors thrown during http requests and formats the responses
| in a consistent manner meant to be easily consumed by clients.
|
| While designed specifically to work with Lucid Resourceful, there is no
| reason it cannot be applied to non-resourceful routes as well.
*/
import { errors } from '@vinejs/vine'
import { default as YAML } from 'yaml'
import { Exception } from '@poppinss/utils'
import { negotiateContent } from '../utils/http'
import { default as string } from '@poppinss/string'
import { TypedEventEmitter } from '@nhtio/tiny-typed-emitter'
import { isObject, stripUndefinedValuesFromObject } from '../../utils'
import { ValidationError as JoiValidationError } from '@nhtio/lucid-resourceful/joi'
import {
  isError,
  isException,
  isJoiValidationError,
  isVineValidationError,
  isNotImplementedError,
} from '../utils/errors'
import type { JsonObject } from 'type-fest'
import type { HttpContext } from '@adonisjs/core/http'
import type { NextFn } from '@adonisjs/core/types/http'
import type { EventMap } from '@nhtio/tiny-typed-emitter'
/**
 * Type alias for Vine validation errors thrown by the VineJS validation library.
 *
 * @example
 * ```typescript
 * try {
 *   await vine.validate(schema, data)
 * } catch (error) {
 *   if (isVineValidationError(error)) {
 *     // Handle Vine validation error
 *   }
 * }
 * ```
 */
export type VineValidationError = InstanceType<typeof errors.E_VALIDATION_ERROR>

/**
 * Event map defining the types of errors that can be emitted by the ResourcefulErrorMiddleware.
 * Each event type maps to the specific error instance that will be passed to event handlers.
 *
 * @example
 * ```typescript
 * const middleware = new ResourcefulErrorMiddleware({
 *   onVineValidationError: (error) => logger.warn('Validation failed', error),
 *   onException: (error) => monitoring.captureException(error),
 *   onAny: (error) => analytics.trackError(error)
 * })
 * ```
 */
export type ResourcefulErrorMiddlewareEvents = EventMap<{
  vineValidationError: [VineValidationError]
  joiValidationError: [JoiValidationError]
  exception: [Exception]
  error: [Error]
  unknown: [unknown]
  any: [unknown]
}>

class ResourcefulErrorMiddlewareEmitter extends TypedEventEmitter<ResourcefulErrorMiddlewareEvents> {}

/**
 * Configuration object for custom HTTP headers to be included in error responses.
 * Allows for CORS headers, security headers, API versioning, or any custom headers
 * that should be consistently applied to all error responses.
 *
 * @example
 * ```typescript
 * const headers: ResourcefulErrorMiddlewareHeaders = {
 *   'X-API-Version': '1.0',
 *   'Access-Control-Allow-Origin': '*',
 *   'X-RateLimit-Remaining': '100'
 * }
 * ```
 */
export interface ResourcefulErrorMiddlewareHeaders {
  [key: string]: string
}

/**
 * Configuration options for the ResourcefulErrorMiddleware class.
 * Defines event handlers for different error types and response formatting options.
 *
 * @example
 * ```typescript
 * const options: ResourcefulErrorMiddlewareOptions = {
 *   onVineValidationError: (error) => logger.warn('Validation failed', error),
 *   onException: (error) => monitoring.captureException(error),
 *   onAny: (error) => analytics.trackError(error),
 *   asYaml: true,
 *   headers: {
 *     'X-API-Version': '1.0',
 *     'Access-Control-Allow-Origin': '*'
 *   }
 * }
 * ```
 */
export interface ResourcefulErrorMiddlewareOptions {
  /** Event handler called when a Vine validation error is encountered */
  onVineValidationError?: (error: VineValidationError) => void
  /** Event handler called when a Joi validation error is encountered */
  onJoiValidationError?: (error: JoiValidationError) => void
  /** Event handler called when a framework Exception is encountered */
  onException?: (error: Exception) => void
  /** Event handler called when a generic Error is encountered */
  onError?: (error: Error) => void
  /** Event handler called when an unknown error type is encountered */
  onUnknown?: (error: unknown) => void
  /** Event handler called for any error that occurs, regardless of type */
  onAny?: (error: unknown) => void
  /** Whether to support YAML response format. Defaults to true */
  asYaml?: boolean
  /** Custom headers to include in all error responses */
  headers?: ResourcefulErrorMiddlewareHeaders
}

/**
 * Individual error detail within a formatted error response.
 * Provides field-level information for validation errors or error chain details.
 *
 * @example
 * ```typescript
 * const detail: ResourcefulFormattedErrorDetails = {
 *   code: 'E_REQUIRED',
 *   message: 'The email field is required',
 *   target: 'email',
 *   context: { field: 'email', value: null }
 * }
 * ```
 */
export interface ResourcefulFormattedErrorDetails {
  /** Machine-readable error code (e.g., 'E_REQUIRED', 'E_INVALID_FORMAT') */
  code: string
  /** Human-readable error message */
  message: string
  /** The field or property that caused the error (for validation errors) */
  target?: string
  /** Additional context information about the error */
  context?: JsonObject
}

/**
 * Standardized error response format used by the ResourcefulErrorMiddleware.
 * Provides consistent structure for all API error responses with support for
 * field-level validation details and error chaining.
 *
 * @example
 * ```typescript
 * const formattedError: ResourcefulFormattedError = {
 *   status: 422,
 *   code: 'E_VALIDATION_ERROR',
 *   message: 'Validation failed',
 *   help: 'Check the provided field values',
 *   details: [
 *     {
 *       code: 'E_REQUIRED',
 *       message: 'The email field is required',
 *       target: 'email'
 *     }
 *   ]
 * }
 * ```
 */
export interface ResourcefulFormattedError {
  /** HTTP status code for the error response */
  status: number
  /** Machine-readable error code */
  code: string
  /** Human-readable error message */
  message: string
  /** Optional help text providing guidance on how to resolve the error */
  help?: string
  /** Array of detailed error information, typically for validation errors */
  details?: ResourcefulFormattedErrorDetails[]
}

/**
 * AdonisJS middleware that intercepts errors thrown during HTTP requests and formats
 * the responses in a consistent, client-friendly manner. Supports multiple error types
 * including Vine validation errors, Joi validation errors, framework exceptions, and
 * generic errors.
 *
 * The middleware provides:
 * - Consistent error response formatting across all error types
 * - Event-driven error handling for monitoring and logging
 * - Content negotiation supporting JSON and YAML responses
 * - Recursive error cause handling to preserve error chains
 * - Configurable headers for CORS, security, and custom requirements
 *
 * @example
 * ```typescript
 * // Basic usage with default configuration
 * const middleware = new ResourcefulErrorMiddleware({})
 * router.use(middleware.handle)
 *
 * // Advanced usage with event handlers and custom headers
 * const middleware = new ResourcefulErrorMiddleware({
 *   onVineValidationError: (error) => logger.warn('Validation failed', error),
 *   onException: (error) => monitoring.captureException(error),
 *   onAny: (error) => analytics.trackError(error),
 *   headers: {
 *     'X-API-Version': '1.0',
 *     'Access-Control-Allow-Origin': '*'
 *   }
 * })
 * router.use(middleware.handle)
 * ```
 *
 * @example
 * ```typescript
 * // Standalone usage outside of resourceful routes
 * import ResourcefulErrorMiddleware from '@nhtio/lucid-resourceful/middlewares/resourceful_error'
 *
 * const errorHandler = new ResourcefulErrorMiddleware({
 *   onException: (error) => logger.error(error),
 *   headers: { 'X-Error-Handler': 'resourceful' }
 * })
 *
 * router.use(errorHandler.handle)
 * ```
 */
export class ResourcefulErrorMiddleware {
  /**
   * Creates a usable middleware function that can be applied directly to AdonisJS routes.
   * @param opts - Configuration options for the middleware
   * @returns A middleware function that can be used in AdonisJS routes
   */
  public static usable(opts: ResourcefulErrorMiddlewareOptions = {}) {
    const instance = new ResourcefulErrorMiddleware(opts)
    return (ctx: HttpContext, next: NextFn) => instance.handle(ctx, next)
  }

  /**
   * Handles errors thrown during request processing. This method is meant to be integrated with the AdonisJS HttpExceptionHandler
   * @param error - The error that was thrown
   * @param ctx - The HTTP context object
   * @param opts - Configuration options for the middleware
   * @returns A Promise that resolves to the error response
   */
  public static handle(
    error: unknown,
    ctx: HttpContext,
    opts: ResourcefulErrorMiddlewareOptions = {}
  ) {
    const instance = new ResourcefulErrorMiddleware(opts)
    return instance.onError(ctx, error)
  }

  /**
   * Determines if the middleware should handle the request based on the desired response format.
   * If the desired format is HTML, it returns false to allow other handlers to process the request.
   * Otherwise, it returns true to indicate that this middleware should handle the error response.
   *
   * @param ctx - The HTTP context object containing request and response information
   * @returns A boolean indicating whether this middleware should handle the request
   */
  public static shouldHandle(ctx: HttpContext): boolean {
    const format = negotiateContent(ctx)
    // if the desired format is HTML, break early since that needs to be handled elsewhere
    return 'html' === format ? false : true
  }

  readonly #emitter: ResourcefulErrorMiddlewareEmitter
  readonly #asYaml: boolean
  readonly #headers: ResourcefulErrorMiddlewareHeaders

  /**
   * Creates a new instance of ResourcefulErrorMiddleware with the specified configuration.
   *
   * @param opts - Configuration options for the middleware
   *
   * @example
   * ```typescript
   * const middleware = new ResourcefulErrorMiddleware({
   *   onVineValidationError: (error) => console.error('Validation:', error),
   *   onException: (error) => monitoring.captureException(error),
   *   asYaml: true,
   *   headers: { 'X-API-Version': '1.0' }
   * })
   * ```
   */
  constructor(opts: ResourcefulErrorMiddlewareOptions) {
    this.#emitter = new ResourcefulErrorMiddlewareEmitter()
    // asYaml defaults to `true`
    this.#asYaml = false !== opts.asYaml
    // headers defaults to an empty object
    this.#headers = isObject(opts.headers) ? opts.headers : {}
    if ('function' === typeof opts.onVineValidationError) {
      this.#emitter.once('vineValidationError', opts.onVineValidationError)
    }
    if ('function' === typeof opts.onJoiValidationError) {
      this.#emitter.once('joiValidationError', opts.onJoiValidationError)
    }
    if ('function' === typeof opts.onException) {
      this.#emitter.once('exception', opts.onException)
    }
    if ('function' === typeof opts.onError) {
      this.#emitter.once('error', opts.onError)
    }
    if ('function' === typeof opts.onUnknown) {
      this.#emitter.once('unknown', opts.onUnknown)
    }
    if ('function' === typeof opts.onAny) {
      this.#emitter.once('any', opts.onAny)
    }
  }

  /**
   * AdonisJS middleware handler that intercepts and processes errors during request execution.
   * This method should be used as middleware in your route definitions.
   *
   * @param ctx - The HTTP context object containing request and response information
   * @param next - The next function to call in the middleware chain
   *
   * @example
   * ```typescript
   * const middleware = new ResourcefulErrorMiddleware({})
   * router.use(middleware.handle)
   *
   * // Or bind it to specific routes
   * router.get('/api/users', UserController.index).use([middleware.handle])
   * ```
   */
  async handle(ctx: HttpContext, next: NextFn) {
    try {
      await next()
    } catch (error) {
      return this.onError(ctx, error)
    }
  }

  /**
   * Processes a caught error, emits appropriate events, and sends a formatted response.
   * This method handles error classification, event emission, content negotiation,
   * and response formatting.
   *
   * @param ctx - The HTTP context object
   * @param error - The error that was caught during request processing
   *
   * @example
   * ```typescript
   * // Typically called automatically by the handle method, but can be used directly
   * try {
   *   // Some operation that might throw
   * } catch (error) {
   *   middleware.onError(ctx, error)
   * }
   * ```
   */
  onError(ctx: HttpContext, error: unknown) {
    this.#emitter.emit('any', error)
    switch (true) {
      case isVineValidationError(error):
        this.#emitter.emit('vineValidationError', error)
        break

      case isJoiValidationError(error):
        this.#emitter.emit('joiValidationError', error)
        break

      case isException(error):
        this.#emitter.emit('exception', error)
        break

      case isError(error):
        this.#emitter.emit('error', error)
        break

      default:
        this.#emitter.emit('unknown', error)
        break
    }
    const format = negotiateContent(ctx)
    // if the desired format is HTML, break early since that needs to be handled elsewhere
    if ('html' === format) return
    const formattedError = this.#getFormattedError(error)
    ctx.response.status(formattedError.status)
    Object.entries(this.#headers).forEach(([key, value]) => {
      ctx.response.header(key, value)
    })
    switch (true) {
      case 'yaml' === format && this.#asYaml:
        return this.#sendAsYaml(ctx, formattedError)

      default:
        return this.#sendAsJson(ctx, formattedError)
    }
  }

  #sendAsJson(ctx: HttpContext, formattedError: ResourcefulFormattedError) {
    ctx.response.header('Content-Type', 'application/json')
    ctx.response.send(JSON.stringify(formattedError))
  }

  #sendAsYaml(ctx: HttpContext, formattedError: ResourcefulFormattedError) {
    ctx.response.header('Content-Type', 'application/x-yaml')
    ctx.response.send(YAML.stringify(formattedError))
  }

  #getFormattedError(error: unknown): ResourcefulFormattedError {
    switch (true) {
      case isVineValidationError(error):
        return this.#formatVineValidationError(error)

      case isJoiValidationError(error):
        return this.#formatJoiValidationError(error)

      case isException(error):
        return this.#formatException(error)

      case isError(error):
        return this.#formatError(error)

      default:
        return this.#formatUnknown(error)
    }
  }

  #formatVineValidationError(error: VineValidationError): ResourcefulFormattedError {
    const status: number = error.status || 422
    const code: string = error.code || 'E_VALIDATION_ERROR'
    const message: string = error.messages || 'Validation failed'
    const help: string | undefined =
      'help' in error && 'string' === typeof error.help ? error.help : undefined
    let details: ResourcefulFormattedErrorDetails[] | undefined = Array.isArray(error.messages)
      ? error.messages.map((m) => {
          return stripUndefinedValuesFromObject({
            code: m.rule ? string.snakeCase(m.rule).toUpperCase() : 'E_VALIDATION_ERROR',
            message: m.message || 'Validation error',
            target: m.field,
          })
        })
      : undefined
    if (error.cause) {
      if (!details) {
        details = []
      }
      const formattedCause = this.#getFormattedError(error.cause)
      details.push({
        code: formattedCause.code,
        message: formattedCause.message,
      })
    }
    return stripUndefinedValuesFromObject({
      status,
      code,
      message,
      help,
      details,
    })
  }

  #formatJoiValidationError(error: JoiValidationError): ResourcefulFormattedError {
    const status: number = 422
    const code: string = 'E_VALIDATION_ERROR'
    const message: string = error.message || 'Validation failed'
    const help: string | undefined =
      'help' in error && 'string' === typeof error.help ? error.help : undefined
    let details: ResourcefulFormattedErrorDetails[] | undefined = Array.isArray(error.details)
      ? error.details.map((d) => {
          return stripUndefinedValuesFromObject({
            code: string.snakeCase(d.type).toUpperCase(),
            message: d.message || 'Validation error',
            target: d.path.length > 0 ? d.path.join('.') : undefined,
            context: d.context || undefined,
          })
        })
      : undefined
    if (error.cause) {
      if (!details) {
        details = []
      }
      const formattedCause = this.#getFormattedError(error.cause)
      details.push({
        code: formattedCause.code,
        message: formattedCause.message,
      })
    }
    return stripUndefinedValuesFromObject({
      status,
      code,
      message,
      help,
      details,
    })
  }

  #formatException(error: Exception): ResourcefulFormattedError {
    const status: number = error.status || 500
    const code: string = error.code || 'E_EXCEPTION'
    const message: string = error.message || 'An exception occurred'
    const help: string | undefined =
      'help' in error && 'string' === typeof error.help ? error.help : undefined
    const formattedCause = error.cause ? this.#getFormattedError(error.cause) : undefined
    const details: ResourcefulFormattedErrorDetails[] | undefined = formattedCause
      ? [
          stripUndefinedValuesFromObject({
            code: formattedCause.code,
            message: formattedCause.message,
          }),
        ]
      : undefined
    return stripUndefinedValuesFromObject({
      status,
      code,
      message,
      help,
      details,
    })
  }

  #formatError(error: Error): ResourcefulFormattedError {
    const status: number = isNotImplementedError(error) ? 501 : 500
    const code: string = 'E_ERROR'
    const message: string = error.message || 'An error occurred'
    const help: string | undefined =
      'help' in error && 'string' === typeof error.help ? error.help : undefined
    const formattedCause = error.cause ? this.#getFormattedError(error.cause) : undefined
    const details: ResourcefulFormattedErrorDetails[] | undefined = formattedCause
      ? [
          stripUndefinedValuesFromObject({
            code: formattedCause.code,
            message: formattedCause.message,
          }),
        ]
      : undefined
    return stripUndefinedValuesFromObject({
      status,
      code,
      message,
      help,
      details,
    })
  }

  #formatUnknown(error: unknown): ResourcefulFormattedError {
    const status: number = 500
    const code: string = 'E_UNKNOWN_ERROR'
    const message: string = 'An unknown error occurred'
    const help: string | undefined =
      isObject(error) && 'help' in error && 'string' === typeof error.help ? error.help : undefined
    const formattedCause =
      isObject(error) && error.cause ? this.#getFormattedError(error.cause) : undefined
    const details: ResourcefulFormattedErrorDetails[] | undefined = formattedCause
      ? [
          stripUndefinedValuesFromObject({
            code: formattedCause.code,
            message: formattedCause.message,
          }),
        ]
      : undefined
    return stripUndefinedValuesFromObject({
      status,
      code,
      message,
      help,
      details,
    })
  }
}
